<template>
  <lf-modal :close="closeModal" large>
    <template #header>
      <div class="flex space-x-2">
        <lf-h1>{{ title }}</lf-h1>
        <lf-badge v-if="offerTemplateName">{{ offerTemplateName }}</lf-badge>
        <lf-badge v-if="funderName">{{ funderName }}</lf-badge>
      </div>
    </template>
    <template #content>
      <form @submit="onSubmit" data-cy="wfb-offer-stips-modal">
        <stips
          ref="stipsList"
          class="px-6 pb-6"
          disable-edit
          hide-title
          :select-only="isSelectOnly"
          :required-stips="stips"
        />
        <div class="flex justify-end border-t border-gray-200 p-6">
          <modal-buttons
            :on-cancel="closeModal"
            save-is-submit
            :disabled="isSubmitting"
          />
        </div>
      </form>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { type PropType, computed, ref } from "vue";

import union from "lodash/union";
import { useForm } from "vee-validate";

import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import Stips from "@/views/deals/components/Stips.vue";

import type { IStip } from "@/models/applications";
import type { IFunder } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";

const props = defineProps({
  stips: {
    type: Array as PropType<IStip[]>,
    required: true,
  },
  title: {
    type: String,
    default: "",
  },
  offerTemplate: {
    type: Object as PropType<OfferTemplate>,
  },
  funder: {
    type: Object as PropType<IFunder>,
  },
  isSelectOnly: {
    type: Boolean,
    default: false,
  },
});

const { handleSubmit, isSubmitting } = useForm({
  initialValues: {
    stips: props.stips,
  },
});

const emit = defineEmits<{
  close: [void];
  stipsUpdated: [number[]];
}>();

const stipsList = ref<InstanceType<typeof Stips> | null>(null);

const funderName = computed(() => props.funder?.full_name ?? "");

const offerTemplateName = computed(() => props.offerTemplate?.name ?? "");

const closeModal = () => {
  emit("close");
};

const onSubmit = handleSubmit(async (values) => {
  let newStipIds = values.stips.map((stip) => stip.id);
  if (stipsList.value) {
    const withCreatedStip = await stipsList.value.externalOnSubmit();
    newStipIds = union(
      newStipIds,
      withCreatedStip.map((stip) => stip.id)
    );
  }
  emit("stipsUpdated", newStipIds);
  closeModal();
});
</script>
